import { useState, useEffect, useRef } from 'react';
import jobService from '../../services/jobs';

interface JobNameAutocompleteProps {
  value: string;
  onChange: (name: string) => void;
  error?: string;
}

function JobNameAutocomplete({ value, onChange, error }: JobNameAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [filtered, setFiltered] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    jobService.getAutocomplete().then(res => {
      if (res.success) setSuggestions(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (value.length > 0) {
      const lower = value.toLowerCase();
      setFiltered(suggestions.filter(s => s.toLowerCase().includes(lower)));
    } else {
      setFiltered(suggestions);
    }
  }, [value, suggestions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm font-medium text-neutral-700 mb-1">Job Name</label>
      <input
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="e.g. Slashing, Pruning, Picking..."
        className={`w-full border rounded-lg px-3 py-2 text-sm ${error ? 'border-red-400' : 'border-neutral-300'} focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(name => (
            <button
              key={name}
              type="button"
              onClick={() => { onChange(name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors"
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default JobNameAutocomplete;
